import { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { BarChart3, FileText, CreditCard, Wallet, LogOut, Receipt, CalendarDays, ListChecks, ClipboardEdit, RefreshCw, Key, X, Users, Banknote, FileCheck, Package, DollarSign, ShoppingBag } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { api } from '../utils/api';

export default function Layout({ children }) {
  const { currentUser, logout, kullanicilar, loadKullanicilar } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Kendi şifre değiştirme
  const [showSifreModal, setShowSifreModal] = useState(false);
  const [eskiSifre, setEskiSifre] = useState('');
  const [yeniSifre, setYeniSifre] = useState('');
  const [yeniSifreTekrar, setYeniSifreTekrar] = useState('');
  const [sifreLoading, setSifreLoading] = useState(false);
  
  // Admin kullanıcı şifre yönetimi
  const [showAdminSifreModal, setShowAdminSifreModal] = useState(false);
  const [selectedKullanici, setSelectedKullanici] = useState('');
  const [adminYeniSifre, setAdminYeniSifre] = useState('');
  const [adminSifreLoading, setAdminSifreLoading] = useState(false);

  // Modal açıldığında kullanıcıları yükle
  useEffect(() => {
    if (showAdminSifreModal && loadKullanicilar) {
      loadKullanicilar();
    }
  }, [showAdminSifreModal]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleSifreDegistir = async () => {
    if (!eskiSifre || !yeniSifre || !yeniSifreTekrar) {
      toast.error('Tüm alanları doldurun');
      return;
    }
    if (yeniSifre !== yeniSifreTekrar) {
      toast.error('Yeni şifreler eşleşmiyor');
      return;
    }
    if (yeniSifre.length < 4) {
      toast.error('Şifre en az 4 karakter olmalı');
      return;
    }

    setSifreLoading(true);
    try {
      await api.sifreDegistir(currentUser.id, eskiSifre, yeniSifre);
      toast.success('Şifre başarıyla değiştirildi!');
      setShowSifreModal(false);
      setEskiSifre('');
      setYeniSifre('');
      setYeniSifreTekrar('');
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Mevcut şifre hatalı');
      } else {
        toast.error('Şifre değiştirilemedi');
      }
    } finally {
      setSifreLoading(false);
    }
  };

  const handleAdminSifreSifirla = async () => {
    if (!selectedKullanici || !adminYeniSifre) {
      toast.error('Kullanıcı seçin ve yeni şifre girin');
      return;
    }
    if (adminYeniSifre.length < 4) {
      toast.error('Şifre en az 4 karakter olmalı');
      return;
    }

    setAdminSifreLoading(true);
    try {
      const res = await api.adminSifreSifirla(currentUser.id, selectedKullanici, adminYeniSifre);
      toast.success(res.data.message);
      setShowAdminSifreModal(false);
      setSelectedKullanici('');
      setAdminYeniSifre('');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Bu işlem için admin yetkisi gerekli');
      } else {
        toast.error('Şifre değiştirilemedi');
      }
    } finally {
      setAdminSifreLoading(false);
    }
  };

  const isActive = (path) => location.pathname === path;
  const isAdmin = currentUser?.rol === 'admin';

  // veri_giris rolü için sadece veri girişi göster
  const isVeriGiris = currentUser?.rol === 'veri_giris' || currentUser?.rol === 'eleman';

  let navItems = [];
  
  if (isVeriGiris) {
    navItems = [
      { path: '/', label: 'Veri Girişi', icon: ClipboardEdit },
    ];
  } else {
    navItems = [
      { path: '/', label: 'Dashboard', icon: BarChart3 },
      { path: '/raporlar', label: 'Raporlar', icon: FileText },
      { path: '/cekler', label: 'Çekler', icon: Receipt },
      { path: '/diger-odemeler', label: 'Diğer Ödemeler', icon: Wallet },
      { path: '/tum-odemeler', label: 'Tüm Ödemeler', icon: ListChecks },
      { path: '/krediler', label: 'Krediler', icon: Banknote },
      { path: '/kredi-kartlari', label: 'Kredi Kartları', icon: CreditCard },
      { path: '/lc', label: 'LC (Akreditif)', icon: FileCheck },
      { path: '/faturalar', label: 'Faturalar', icon: FileText },
      { path: '/siparisler', label: 'Siparişler', icon: Package },
      { path: '/gider-gelir', label: 'Günlük Kasa', icon: DollarSign },
      { path: '/trendyol', label: 'Trendyol', icon: ShoppingBag },
    ];

    if (isAdmin) {
      navItems.push({ path: '/bankalar', label: 'Bankalar', icon: CalendarDays });
    }
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Kendi Şifre Değiştirme Modal */}
      {showSifreModal && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="text-lg font-bold text-textPrimary flex items-center gap-2">
                <Key className="h-5 w-5" />
                Şifre Değiştir
              </h2>
              <button 
                onClick={() => setShowSifreModal(false)}
                className="p-1 hover:bg-surfaceSubtle rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-1">
                  Mevcut Şifre
                </label>
                <Input
                  type="password"
                  value={eskiSifre}
                  onChange={(e) => setEskiSifre(e.target.value)}
                  placeholder="Mevcut şifrenizi girin"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-1">
                  Yeni Şifre
                </label>
                <Input
                  type="password"
                  value={yeniSifre}
                  onChange={(e) => setYeniSifre(e.target.value)}
                  placeholder="Yeni şifrenizi girin"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-1">
                  Yeni Şifre (Tekrar)
                </label>
                <Input
                  type="password"
                  value={yeniSifreTekrar}
                  onChange={(e) => setYeniSifreTekrar(e.target.value)}
                  placeholder="Yeni şifrenizi tekrar girin"
                />
              </div>
            </div>
            <div className="flex gap-2 p-4 border-t border-border">
              <Button
                variant="outline"
                onClick={() => setShowSifreModal(false)}
                className="flex-1"
              >
                İptal
              </Button>
              <Button
                onClick={handleSifreDegistir}
                disabled={sifreLoading}
                className="flex-1 bg-primary"
              >
                {sifreLoading ? 'Kaydediliyor...' : 'Kaydet'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Kullanıcı Şifre Yönetimi Modal */}
      {showAdminSifreModal && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="text-lg font-bold text-textPrimary flex items-center gap-2">
                <Users className="h-5 w-5" />
                Kullanıcı Şifre Yönetimi
              </h2>
              <button 
                onClick={() => setShowAdminSifreModal(false)}
                className="p-1 hover:bg-surfaceSubtle rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-1">
                  Kullanıcı Seçin
                </label>
                <select
                  value={selectedKullanici}
                  onChange={(e) => setSelectedKullanici(e.target.value)}
                  className="w-full h-9 px-3 py-2 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-1 focus:ring-ring"
                >
                  <option value="">Kullanıcı seçin</option>
                  {kullanicilar && kullanicilar.filter(k => k.id !== currentUser?.id).map((k) => (
                    <option key={k.id} value={k.id}>
                      {k.ad} ({k.rol === 'admin' ? 'Yönetici' : 'Personel'})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-1">
                  Yeni Şifre
                </label>
                <Input
                  type="password"
                  value={adminYeniSifre}
                  onChange={(e) => setAdminYeniSifre(e.target.value)}
                  placeholder="Yeni şifre girin"
                />
              </div>
              <p className="text-xs text-textSecondary">
                Bu işlem seçilen kullanıcının şifresini değiştirecektir.
              </p>
            </div>
            <div className="flex gap-2 p-4 border-t border-border">
              <Button
                variant="outline"
                onClick={() => setShowAdminSifreModal(false)}
                className="flex-1"
              >
                İptal
              </Button>
              <Button
                onClick={handleAdminSifreSifirla}
                disabled={adminSifreLoading}
                className="flex-1 bg-primary"
              >
                {adminSifreLoading ? 'Kaydediliyor...' : 'Şifreyi Değiştir'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className="hidden md:fixed md:inset-y-0 md:flex md:w-64 md:flex-col">
        <div className="flex flex-col flex-grow border-r border-border bg-surface">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 flex-shrink-0 px-4 border-b border-border">
            <img 
              src="https://customer-assets.emergentagent.com/job_branch-eod-system/artifacts/oajdv090_IMG_1236.png" 
              alt="MİBA GRUP" 
              className="h-8 w-auto object-contain"
            />
            <button
              onClick={() => {
                window.location.reload();
                toast.success('Sayfa yenileniyor...');
              }}
              className="p-2 text-textSecondary hover:text-primary hover:bg-surfaceSubtle rounded-full transition-colors"
              title="Sayfayı Yenile"
              data-testid="refresh-page-btn"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>

          {/* User Info */}
          <div className="px-6 py-4 border-b border-border">
            <p className="text-sm font-medium text-textPrimary">{currentUser?.ad}</p>
            <p className="text-xs text-textSecondary capitalize">{currentUser?.rol === 'admin' ? 'Yönetici' : currentUser?.rol?.replace('_', ' ')}</p>
            <div className="flex flex-col gap-1 mt-2">
              <button
                onClick={() => setShowSifreModal(true)}
                className="text-xs text-primary hover:underline flex items-center gap-1"
              >
                <Key className="h-3 w-3" />
                Şifremi Değiştir
              </button>
              {isAdmin && (
                <button
                  onClick={() => setShowAdminSifreModal(true)}
                  className="text-xs text-amber-600 hover:underline flex items-center gap-1"
                >
                  <Users className="h-3 w-3" />
                  Kullanıcı Şifreleri
                </button>
              )}
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  className={`w-full flex items-center px-4 py-3 text-sm font-medium transition-all ${
                    isActive(item.path)
                      ? 'bg-primary text-primaryForeground'
                      : 'text-textSecondary hover:bg-surfaceSubtle hover:text-textPrimary'
                  }`}
                >
                  <Icon className="mr-3 h-5 w-5" strokeWidth={1.5} />
                  {item.label}
                </button>
              );
            })}
          </nav>

          {/* Logout */}
          <div className="px-4 py-4 border-t border-border">
            <Button
              onClick={handleLogout}
              data-testid="logout-btn"
              variant="outline"
              className="w-full justify-start text-textSecondary hover:text-textPrimary"
            >
              <LogOut className="mr-3 h-5 w-5" strokeWidth={1.5} />
              Çıkış Yap
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Header with Menu Button */}
      <div className="md:hidden fixed top-0 inset-x-0 bg-surface border-b border-border z-50">
        <div className="flex items-center justify-between h-14 px-4">
          <img 
            src="https://customer-assets.emergentagent.com/job_branch-eod-system/artifacts/oajdv090_IMG_1236.png" 
            alt="MİBA GRUP" 
            className="h-8 w-auto"
          />
          <button
            onClick={() => navigate('/menu')}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg font-medium"
          >
            <BarChart3 className="h-5 w-5" />
            Menü
          </button>
        </div>
      </div>

      {/* Main Content */}
      <main className="md:pl-64 pt-16 md:pt-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-8">
          {children}
        </div>
      </main>
    </div>
  );
}
